/**
 * QUIPWA — composition root
 *
 * Builds every long-lived service once and wires them together. React reaches
 * them through DBProvider; tests build their own with fakes. Nothing else in
 * the app should `new` these classes.
 *
 *   ┌──────────── local ─────────────┐   ┌──────────── sync ─────────────┐
 *   │ state DB ── StateDBService     │   │ StateReplicator (state DB)    │
 *   │ local DB ── KvStore, Outbox,   │──►│ DeckSync, DeckUploads         │──► Drive
 *   │             notes (repository) │   │ NotesDriveSync (notes)        │
 *   │ collections (registry)         │   │ outbox handlers               │
 *   └────────────────────────────────┘   │ SyncEngine (when / order)     │
 *                                        └───────────────────────────────┘
 */

import { openLocalDB, openStateDB, type DBAdapterType } from './db/db-factory.ts'
import type { SqlJsAdapter } from './db/sqljs-adapter.ts'
import { StateDBService } from './db/state-db-service.ts'
import { getCollection } from './db/collection-db-service.ts'
import { KvStore } from './local/kv-store.ts'
import { Outbox } from './local/outbox.ts'
import { driveService, type DriveService } from './drive/drive-service.ts'
import { WFlashImporter } from './wflash/wflash-importer.ts'
import { getDeviceClock } from './replication/device-id.ts'
import { StateReplicator } from './sync/state-replicator.ts'
import { DeckSync } from './sync/deck-sync.ts'
import { DECK_UPLOAD, DeckUploads } from './sync/deck-uploads.ts'
import { defaultBlobStore } from './db/idb-blob-store.ts'
import { SyncEngine, blockedStatus, type AuthAvailability, type OutboxHandler, type SyncEngineDeps, type SyncReport } from './sync/sync-engine.ts'
import { useSyncStatus } from './sync/sync-status-store.ts'
import { authSession } from './auth/index.ts'
import { NotesTable } from './notes/notes-table.ts'
import { DriveIdPool } from './notes/drive-id-pool.ts'
import { LocalNotesRepository, NOTE_PUSH } from './notes/local-notes-repository.ts'
import { NotesDriveSync } from './notes/notes-drive-sync.ts'
import { newLocalNoteId } from './notes/note-model.ts'
import { useNoteConflicts } from '../stores/note-conflicts-store.ts'
import type { ExamRepository } from './exam/exam-repository.ts'

export interface AppServices {
  stateAdapter: SqlJsAdapter
  localAdapter: SqlJsAdapter
  engine: DBAdapterType
  state: StateDBService
  /** Notes on this device (what the notes UI reads and writes). */
  notes: LocalNotesRepository
  notesSync: NotesDriveSync
  /**
   * Socratic exam: bank, attempts and events in the replicated state. Loaded
   * on demand: its validation (zod) must not weigh on the first screen.
   */
  exam: () => Promise<ExamRepository>
  kv: KvStore
  outbox: Outbox
  drive: DriveService
  importer: WFlashImporter
  deckSync: DeckSync
  /** Queue a deck's upload after any local change to its cards. */
  deckUploads: DeckUploads
  sync: SyncEngine
  /** Register a handler for an outbox op kind (notes, decks… register theirs). */
  registerOutboxHandler: (kind: string, handler: OutboxHandler) => void
  close: () => Promise<void>
}

export interface AppServicesOptions {
  onLocalDataChanged?: (report: SyncReport) => void
}

/** Map the auth state machine onto what the sync engine needs to know. */
export function authAvailability(): AuthAvailability {
  switch (authSession.state.status) {
    case 'active':
      return 'active'
    case 'expired':
      return 'expired'
    case 'needs_consent':
      return 'needs_consent'
    default:
      return 'signed_out'
  }
}

/**
 * Status to show before the engine's first run. The store starts at
 * "synced", which a signed-out or offline device would show until the first
 * sync attempt corrected it (and the layout jumped when it did).
 */
export function seedSyncStatus(online: boolean = typeof navigator === 'undefined' || navigator.onLine !== false): void {
  useSyncStatus.getState().set({ status: blockedStatus(authAvailability(), online) ?? 'syncing' })
}

/** Outbox op kinds handled at this level. */
export const OUTBOX = {
  /** Move a Drive file to the trash (deleted deck, deleted note). Payload: { fileId }. */
  driveTrash: 'drive.trash',
} as const

export async function createAppServices(options: AppServicesOptions = {}): Promise<AppServices> {
  const [{ adapter: stateAdapter, engine }, { adapter: localAdapter }] = await Promise.all([openStateDB(), openLocalDB()])

  const clock = getDeviceClock()
  const state = new StateDBService(stateAdapter, clock)
  const kv = new KvStore(localAdapter)
  const outbox = new Outbox(localAdapter)
  const importer = new WFlashImporter(state)

  const replicator = new StateReplicator({
    drive: driveService,
    state: stateAdapter,
    kv,
    clock,
    rootFolderId: () => driveService.ensureRootFolder(),
  })
  const deckUploads = new DeckUploads({
    drive: driveService,
    state,
    outbox,
    kv,
    blobs: defaultBlobStore,
    getCollection,
    onWrite: () => sync.requestSync('local-change'),
  })
  const deckSync = new DeckSync({
    drive: driveService,
    state,
    importer,
    kv,
    getCollection,
    hasPendingUpload: (id) => deckUploads.hasPendingUpload(id),
  })

  const handlers: Record<string, OutboxHandler> = {
    [OUTBOX.driveTrash]: async (op) => {
      const { fileId } = op.payload as { fileId: string }
      await driveService.trashFile(fileId)
    },
  }

  const engineDeps: SyncEngineDeps = {
    state: replicator,
    decks: deckSync,
    outbox,
    kv,
    handlers,
    auth: authAvailability,
    setStatus: (patch) => useSyncStatus.getState().set(patch),
    ...(options.onLocalDataChanged ? { onLocalDataChanged: options.onLocalDataChanged } : {}),
  }
  const sync = new SyncEngine(engineDeps)

  // Notes: local-first repository + its Drive sync (outbox handler and phase).
  const notesTable = new NotesTable(localAdapter)
  const idPool = new DriveIdPool(kv)
  const notes = new LocalNotesRepository({
    table: notesTable,
    outbox,
    nextId: async () => (await idPool.take()) ?? newLocalNoteId(),
    onWrite: () => sync.requestSync('local-change'),
  })
  const notesSync = new NotesDriveSync({
    drive: driveService,
    table: notesTable,
    outbox,
    idPool,
    onChanged: () => notes.notifyChanged(),
    onConflict: (notice) => useNoteConflicts.getState().add(notice),
  })
  handlers[NOTE_PUSH] = (op) => notesSync.push((op.payload as { id: string }).id)
  handlers[DECK_UPLOAD] = (op) => deckUploads.push((op.payload as { deckInstanceId: string }).deckInstanceId)
  engineDeps.notes = { sync: () => notesSync.pull() }

  // Local writes to the replicated state → sync soon (debounced).
  stateAdapter.onChange(() => sync.requestSync('local-change'))
  // A usable session (sign-in, reconnect, permission granted) → sync now.
  let lastAuth = authAvailability()
  const unsubscribeAuth = authSession.subscribe(() => {
    const next = authAvailability()
    if (next === 'active' && lastAuth !== 'active') sync.requestSync('auth', { urgent: true })
    const blocked = blockedStatus(next, true)
    if (next !== lastAuth && blocked) useSyncStatus.getState().set({ status: blocked })
    lastAuth = next
  })

  const lastSyncedAt = await kv.get<number>('sync.lastSyncedAt')
  if (lastSyncedAt) useSyncStatus.getState().set({ lastSyncedAt })

  let exam: Promise<ExamRepository> | null = null

  return {
    stateAdapter,
    localAdapter,
    engine,
    state,
    notes,
    notesSync,
    exam: () => (exam ??= import('./exam/exam-repository.ts').then((m) => new m.ExamRepository(stateAdapter, clock))),
    kv,
    outbox,
    drive: driveService,
    importer,
    deckSync,
    deckUploads,
    sync,
    registerOutboxHandler: (kind, handler) => {
      handlers[kind] = handler
    },
    close: async () => {
      unsubscribeAuth()
      sync.stop()
      await Promise.all([stateAdapter.close(), localAdapter.close()])
    },
  } satisfies AppServices
}
